// src/components/VideoBgTool.js
import React, { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { removeVideoBgFromFile } from "../services/apiService";
import { IoCloudUploadOutline, IoDownloadOutline } from 'react-icons/io5';

const Loader = () => (
  <div className="flex flex-col items-center justify-center text-center">
    <div className="w-12 h-12 border-4 border-dark-accent-start border-t-transparent rounded-full animate-spin"></div>
    <p className="mt-4 text-sm text-dark-text-secondary">Processing video, this may take a moment...</p>
  </div>
);

const VideoBgTool = () => {
  const [videos, setVideos] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const processFile = useCallback(async (file) => {
    if (!file.type.startsWith('video/')) {
        alert('Please upload a valid video file.');
        return;
    }
    const id = Date.now() + Math.random();
    const previewUrl = URL.createObjectURL(file);
    setVideos(prev => [{ id, name: file.name, isLoading: true, previewUrl, processedUrl: null }, ...prev]);
    setSelectedId(id);

    try {
      const blob = await removeVideoBgFromFile(file);
      const processedUrl = URL.createObjectURL(blob);
      setVideos(prev => prev.map(item => item.id === id ? { ...item, processedUrl, isLoading: false } : item));
    } catch (error) {
      console.error("Error removing video background:", error);
      alert(`Failed to remove background from ${file.name}. Please try again.`);
      setVideos(prev => prev.filter(item => item.id !== id));
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const selectedVideo = videos.find(item => item.id === selectedId);

  return (
    <div className="mx-auto max-w-4xl px-6">
      <div 
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="relative flex flex-col items-center justify-center w-full min-h-[400px] bg-dark-card/40 backdrop-blur-lg border-2 border-dashed border-white/10 rounded-3xl p-8 text-center"
      >
        {videos.length === 0 ? (
          <>
            <IoCloudUploadOutline className="h-16 w-16 text-dark-text-secondary/50 mb-4" />
            <h3 className="text-xl font-semibold text-dark-text-primary">Drag & drop a video here</h3>
            <p className="mt-2 text-dark-text-secondary">or</p>
            <button 
              onClick={() => fileInputRef.current.click()}
              className="mt-4 rounded-md bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90"
            >
              Upload Video
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="video/*" className="hidden" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {selectedVideo?.isLoading ? (
              <Loader />
            ) : (
              selectedVideo?.processedUrl && (
                <>
                  <video src={selectedVideo.processedUrl} controls className="max-h-[350px] max-w-full rounded-lg" />
                  <a
                    href={selectedVideo.processedUrl}
                    download={`${selectedVideo.name.split('.')[0]}-bgremoved.mp4`}
                    className="absolute bottom-6 right-6 flex items-center gap-2 rounded-full bg-dark-accent-start px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:opacity-90"
                  >
                    <IoDownloadOutline size={16} />
                    Download
                  </a>
                </>
              )
            )}
          </div>
        )}
      </div>

      {videos.length > 0 && (
        <div className="mt-8">
          <h4 className="text-sm font-semibold text-dark-text-primary mb-4">Your Videos</h4>
          <div className="flex flex-wrap gap-4">
            {videos.map(vid => (
              <div 
                key={vid.id} 
                onClick={() => setSelectedId(vid.id)}
                className={`relative w-24 h-24 rounded-lg cursor-pointer overflow-hidden transition-all duration-300
                  ${selectedId === vid.id ? 'ring-2 ring-offset-2 ring-offset-dark-bg ring-dark-accent-start' : 'ring-1 ring-white/10'}`
                }
              >
                <video src={vid.previewUrl} className="w-full h-full object-cover" muted playsInline />
                {vid.isLoading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div></div>}
              </div>
            ))}
             <button 
              onClick={() => fileInputRef.current.click()}
              className="w-24 h-24 flex items-center justify-center rounded-lg border-2 border-dashed border-white/20 text-dark-text-secondary/50 hover:border-white/40 hover:text-white/80 transition-colors"
            >
              <Plus size={24} />
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="video/*" className="hidden" />
          </div>
        </div>
      )}

      <p className="mt-12 text-xs text-center text-dark-text-secondary/50 max-w-xl mx-auto">
        By uploading a video you agree to our <Link to="/terms" className="underline hover:text-white">Terms of Service</Link>. 
        This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
      </p>
    </div>
  );
};

const Plus = ({ size }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

export default VideoBgTool;